import { KEYS } from "@/src/const";
import { GoogleMaps } from "@/src/service";
import { TrackingParams, GetTrackingParamsResponse, StoredLocationData, trackingParamNames } from "@/src/type/tracking";
import { StrapiCollectionType } from "@/src/type";
import { UUIDHelper, Log, StorageHelper, CookieHelper } from "@/src/util";

const STORAGE_TRACKING_PARAMS = "STORAGE_TRACKING_PARAMS";

function paramNameExchange(paramName: string): string {
  switch (paramName) {
    case "utm_source":
      return "utmSource";
    case "utm_medium":
      return "utmMedium";
    case "utm_campaign":
      return "utmCampaign";
    case "utm_term":
      return "utmTerm";
    case "utm_content":
      return "utmContent";
    default:
      return paramName;
  }
}

function addParameters(path: string, parameters: string) {
  if (!parameters) {
    return path;
  }
  return path.includes("?") ? `${path}&${parameters}` : `${path}?${parameters}`;
}

function carryUTMParams(pathAndQuery: string, active: boolean = true, utmParams: any) {
  if (!active || !pathAndQuery) {
    return pathAndQuery ?? "";
  }

  const params = new URLSearchParams();
  if (utmParams?.utmSource) {
    params.set("utm_source", utmParams.utmSource);
  }
  if (utmParams?.utmMedium) {
    params.set("utm_medium", utmParams.utmMedium);
  }
  if (utmParams?.utmCampaign) {
    params.set("utm_campaign", utmParams.utmCampaign);
  }
  if (utmParams?.utmContent) {
    params.set("utm_content", utmParams.utmContent);
  }
  if (utmParams?.utmTerm) {
    params.set("utm_term", utmParams.utmTerm);
  }
  if (utmParams?.facebookCLID) {
    params.set("fbclid", utmParams.facebookCLID);
  }
  if (utmParams?.googleCLID) {
    params.set("gclid", utmParams.googleCLID);
  }

  return addParameters(pathAndQuery, params.toString());
}

async function getLocationData(): Promise<StoredLocationData | undefined> {
  const storedLocation = localStorage.getItem(KEYS.LOCAL_STORAGE.LOCATION_DATA);
  if (!storedLocation) {
    Log.logToConsoleDebug("TrackingLogic.ts:(getLocationData)", "refreshing location data");
    return await refreshLocationData();
  }

  const parsedLocation = JSON.parse(storedLocation);
  const dataCreatedAt = new Date(parsedLocation.createdAt);

  const oneDayAgo = new Date().getTime() - 24 * 60 * 60 * 1000;
  const responseData = dataCreatedAt.getTime() <= oneDayAgo ? await refreshLocationData() : parsedLocation;
  if (responseData) {
    responseData.createdAt = undefined;
  }
  return responseData;
}

async function refreshLocationData() {
  try {
    const geoLocateResponse = await GoogleMaps.geoLocate();
    if (!geoLocateResponse.success) {
      Log.logToConsoleDebug("TrackingLogic.ts:(refreshLocationData)", "Google Maps GeoLocate Error", [geoLocateResponse]);
      return undefined;
    }
    const reverseGeoCodingResponse = await GoogleMaps.getReverseGeoCoding(geoLocateResponse.location);
    if (!reverseGeoCodingResponse.success) {
      return undefined;
    }
    const parsedAddress = GoogleMaps.parseAddressComponents(reverseGeoCodingResponse.location.results[0]);
    if (!parsedAddress) {
      Log.logToConsoleDebug(
        "TrackingLogic.ts:(refreshLocationData)",
        "Error parsing Google Maps address components. Reverse GeoCoding Response: ",
        [reverseGeoCodingResponse],
        Log.LOG_COLORS.ERROR.BG_COLOR,
        Log.LOG_COLORS.ERROR.COLOR
      );
      return undefined;
    }

    const location: StoredLocationData = {
      latitude: geoLocateResponse.location.lat,
      longitude: geoLocateResponse.location.lng,
      city: parsedAddress.city,
      state: parsedAddress.state,
      country: parsedAddress.country,
      createdAt: new Date(),
    };

    localStorage.setItem(KEYS.LOCAL_STORAGE.LOCATION_DATA, JSON.stringify(location));
    return location;
  } catch (error) {
    Log.logToProvider(Log.LogLevel.WARN, "TrackingLogic.ts:(refreshLocationData)", "Error refreshing location data", [error]);
    return undefined;
  }
}

function getDeviceId(): string {
  const deviceId = localStorage.getItem(KEYS.LOCAL_STORAGE.DEVICE_ID);
  if (!deviceId) {
    const newDeviceId = UUIDHelper.createUUID();
    localStorage.setItem(KEYS.LOCAL_STORAGE.DEVICE_ID, newDeviceId);
    return newDeviceId;
  }
  return deviceId;
}

const getSessionId = async () => {
  if (typeof window === "undefined") {
    return "";
  }

  const EXPIRATION = 6 * 60 * 60 * 1000;
  const id = await StorageHelper().getFromCache("SESSION_ID");
  if (typeof id === "string" && id.length > 1) {
    StorageHelper().setToCache("SESSION_ID", `${id}`, EXPIRATION);
    return id;
  }

  const newId = UUIDHelper.createUUID();
  StorageHelper().setToCache("SESSION_ID", `${newId}`, EXPIRATION);
  return newId;
};

const getPersistentId = () => {
  return CookieHelper.getCookie("PERSISTENT_ID");
};

function getUTMParams(url?: string | null, referrer?: string | null): GetTrackingParamsResponse {
  if (typeof window === "undefined") {
    return {};
  }

  const search = url?.includes("?") ? url.split("?")[1] : undefined;
  const searchParams = new URLSearchParams(search ?? window.location.search);

  const newParamsExist: boolean =
    typeof searchParams.get("utm_medium") === "string" ||
    typeof searchParams.get("utm_source") === "string" ||
    typeof searchParams.get("utm_campaign") === "string" ||
    typeof searchParams.get("utm_term") === "string" ||
    typeof searchParams.get("utm_content") === "string" ||
    typeof searchParams.get("marketingId") === "string" ||
    typeof searchParams.get("fbclid") === "string" ||
    typeof searchParams.get("gclid") === "string";

  const storedParams = localStorage.getItem(STORAGE_TRACKING_PARAMS);
  if (storedParams && !newParamsExist) {
    return JSON.parse(storedParams);
  }

  const params: GetTrackingParamsResponse = {
    referrer: referrer ?? document.referrer,
    utmContent: searchParams.get("utm_content") ?? undefined,
    utmTerm: searchParams.get("utm_term") ?? undefined,
    utmMedium: searchParams.get("utm_medium") ?? undefined,
    utmSource: searchParams.get("utm_source") ?? undefined,
    utmCampaign: searchParams.get("utm_campaign") ?? undefined,
    marketingId: searchParams.get("marketingId") ?? undefined,
    googleCLID: searchParams.get("gclid") ?? undefined,
    facebookCLID: searchParams.get("fbclid") ?? undefined,
  };
  localStorage.setItem(STORAGE_TRACKING_PARAMS, JSON.stringify(params));
  return params;
}

function getUTMQueryString(utmParams?: GetTrackingParamsResponse): string {
  const arr = [];
  if (utmParams?.utmSource) {
    arr.push(`utm_source=${utmParams.utmSource}`);
  }
  if (utmParams?.utmMedium) {
    arr.push(`utm_medium=${utmParams.utmMedium}`);
  }
  if (utmParams?.utmCampaign) {
    arr.push(`utm_campaign=${utmParams.utmCampaign}`);
  }
  if (utmParams?.utmTerm) {
    arr.push(`utm_term=${utmParams.utmTerm}`);
  }
  if (utmParams?.utmContent) {
    arr.push(`utm_content=${utmParams.utmContent}`);
  }
  return `?${arr.join("&")}`;
}

function getTrackingParams(): TrackingParams {
  const defaultParams: TrackingParams = {
    utm_source: getUTMParams().utmSource,
    utm_medium: getUTMParams().utmMedium,
    utm_campaign: getUTMParams().utmCampaign,
    utm_term: getUTMParams().utmTerm,
    utm_content: getUTMParams().utmContent,
    marketingId: getUTMParams().marketingId,
    fbclid: getUTMParams().facebookCLID,
    gclid: getUTMParams().googleCLID,
    utmQueryString: getUTMQueryString(getUTMParams()),
    sourceName: getMarketingSource(),
    sourcePhoneNumber: "",
    referrer: getUTMParams().referrer,
  };

  if (typeof sessionStorage === "undefined") {
    return defaultParams;
  }

  const json = sessionStorage.getItem(STORAGE_TRACKING_PARAMS);
  return json ? JSON.parse(json) : defaultParams;
}

function addTrackingParamsToLink(link: string, customParams?: Partial<TrackingParams>) {
  const trackingParams = customParams ? customParams : getTrackingParams();
  const trackingParamsString = trackingParamNames
    .filter((paramName) => trackingParams[paramName])
    .map((paramName) => `${paramName}=${trackingParams[paramName]}`)
    .join("&");

  return addParameters(link, trackingParamsString);
}

/**
 * Marketing Source Tracking
 */

function getMarketingSource() {
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem(KEYS.LOCAL_STORAGE.MARKETING_SOURCE) ?? "";
  }
  return "";
}

function checkUTMParams(toCheck?: string, currentValue?: string) {
  if (!toCheck) {
    return true;
  }
  return toCheck === (currentValue ?? "_____FAILED_____");
}

function setMarketingSource(utmParams?: GetTrackingParamsResponse, marketingSources?: StrapiCollectionType.MarketingSource[]) {
  for (const source of marketingSources ?? []) {
    for (const params of source.utmParams ?? []) {
      if (
        checkUTMParams(params?.utmSource, utmParams?.utmSource) &&
        checkUTMParams(params?.utmMedium, utmParams?.utmMedium) &&
        checkUTMParams(params?.utmCampaign, utmParams?.utmCampaign) &&
        checkUTMParams(params?.utmTerm, utmParams?.utmTerm) &&
        checkUTMParams(params?.utmContent, utmParams?.utmContent)
      ) {
        if (source.name && typeof localStorage !== "undefined") {
          localStorage.setItem(KEYS.LOCAL_STORAGE.MARKETING_SOURCE, source.name);
          return source.name;
        }
        break;
      }
    }
  }

  try {
    localStorage.removeItem(KEYS.LOCAL_STORAGE.MARKETING_SOURCE);
  } catch (err) {}
}

function getMarketingPhoneNumber(marketingSources?: StrapiCollectionType.MarketingSource[], propertySlug?: string) {
  const marketingSource = getMarketingSource();
  if (!marketingSource || !propertySlug) {
    return;
  }

  for (const source of marketingSources ?? []) {
    if (source.name === marketingSource) {
      for (const phoneNumber of source.marketing_phone_numbers?.data ?? []) {
        if (propertySlug === phoneNumber.attributes?.property?.data?.attributes?.slug) {
          return {
            phoneNumber: phoneNumber.attributes?.phoneNumber,
            countryCode: phoneNumber.attributes?.countryCode,
          };
        }
      }
    }
  }
}

/**
 * Component Tracking
 */

interface ConstructToSectionTypePathProps {
  pageId: string | number;
  sectionId: string | number;
  sectionType: string;
  sectionTypeId: string | number;
}

const constructToSectionTypePath = ({ pageId, sectionId, sectionType, sectionTypeId }: ConstructToSectionTypePathProps) => {
  return `p___${pageId};s___${sectionId};t___${sectionType}___${sectionTypeId}`;
};

interface ConstructComponentIdProps {
  sectionTypePath?: string;
  componentType?: string;
  componentId?: string | number;
  pathToElement?: string;
}

const constructComponentId = ({ sectionTypePath, componentType, componentId, pathToElement }: ConstructComponentIdProps) => {
  if (!sectionTypePath || !pathToElement || !componentType || !componentId) {
    return undefined;
  }
  return `${sectionTypePath};e___${pathToElement};c___${componentType}___${componentId}`;
};

interface DeconstructComponentIdResponse {
  pageId: string;
  dynamicSectionId: string;
  sectionId: string;
  sectionType: string;
  componentId: string;
  componentType: string;
  pathToElement: string;
}

function deconstructComponentId(id?: string | null): DeconstructComponentIdResponse {
  if (!id) {
    return {
      pageId: "",
      dynamicSectionId: "",
      sectionId: "",
      sectionType: "",
      componentId: "",
      componentType: "",
      pathToElement: "",
    };
  }

  const parts = id.split(";");
  const deconstructed: DeconstructComponentIdResponse = {
    pageId: "",
    dynamicSectionId: "",
    sectionId: "",
    sectionType: "",
    componentId: "",
    componentType: "",
    pathToElement: "",
  };

  for (const part of parts) {
    const [key, typeOrValue, value] = part.split("___");
    switch (key) {
      case "p":
        deconstructed.pageId = typeOrValue;
        break;
      case "s":
        deconstructed.dynamicSectionId = typeOrValue;
        break;
      case "t":
        deconstructed.sectionType = typeOrValue;
        deconstructed.sectionId = value;
        break;
      case "e":
        deconstructed.pathToElement = typeOrValue;
        break;
      case "c":
        deconstructed.componentType = typeOrValue;
        deconstructed.componentId = value;
        break;
      default:
        break;
    }
  }

  return deconstructed;
}

export default {
  getPersistentId,
  getUTMParams,
  getUTMQueryString,
  getTrackingParams,
  addTrackingParamsToLink,
  getMarketingSource,
  setMarketingSource,
  getLocationData,
  getDeviceId,
  getSessionId,
  paramNameExchange,
  carryUTMParams,
  getMarketingPhoneNumber,
  constructComponentId,
  constructToSectionTypePath,
  addParameters,
  deconstructComponentId,
};
